import { ChampsFusion } from '@core/models/champsFusion.model';

export interface RawChampsFusion {
  Id?: unknown;
  id?: unknown;
  ChampFusion?: unknown;
  champFusion?: unknown;
  FieldName?: unknown;
  fieldName?: unknown;
  InternalName?: unknown;
  internalName?: unknown;
  Sequence?: unknown;
  sequence?: unknown;
  IsPaveAdresse?: unknown;
  isPaveAdresse?: unknown;
}

interface IndexedChampsFusion {
  field: ChampsFusion;
  originalIndex: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeBusinessValue(value: unknown): string | null {
  const normalized = String(value ?? '').trim();
  const lowerCaseValue = normalized.toLowerCase();
  return normalized && lowerCaseValue !== 'null' && lowerCaseValue !== 'undefined'
    ? normalized
    : null;
}

function normalizeSequence(value: unknown, fallback: number): number {
  const normalized = normalizeBusinessValue(value);
  const sequence = normalized === null ? Number.NaN : Number(normalized);
  return Number.isFinite(sequence) ? sequence : fallback;
}

function getRawFields(response: unknown): unknown[] {
  if (Array.isArray(response)) {
    return response;
  }
  if (!isRecord(response)) {
    return [];
  }

  const fields = response['ChampsFusion'] ?? response['champsFusion'];
  if (Array.isArray(fields)) {
    return fields;
  }
  return isRecord(fields) ? [fields] : [];
}

export function normalizePaveAdresseFlag(value: unknown): 'true' | 'false' {
  const normalizedValue = String(value ?? '').trim().toLowerCase();
  return value === true || value === 1 || normalizedValue === 'true' || normalizedValue === '1'
    ? 'true'
    : 'false';
}

export function isPaveAdresseField(field: RawChampsFusion): boolean {
  return normalizePaveAdresseFlag(field?.IsPaveAdresse ?? field?.isPaveAdresse) === 'true';
}

/**
 * Returns a stable business identity for a fusion field.
 *
 * Standalone exposes every CSV column with Id=0/internalName="null", and the
 * persisted endpoint can expose Id="null". Those transport placeholders must
 * never be used to merge two different columns.
 */
export function getChampFusionIdentityKey(field: RawChampsFusion): string | null {
  const internalName = normalizeBusinessValue(field?.internalName ?? field?.InternalName);
  if (internalName) {
    return `internal:${internalName}`;
  }

  const fieldName = normalizeBusinessValue(
    field?.ChampFusion ?? field?.champFusion ?? field?.FieldName ?? field?.fieldName
  );
  if (fieldName) {
    return `field:${fieldName}`;
  }

  const id = normalizeBusinessValue(field?.Id ?? field?.id);
  return id && id !== '0' ? `id:${id}` : null;
}

function getPersistedFieldKey(field: ChampsFusion): string | null {
  const identity = getChampFusionIdentityKey(field);
  return identity
    ? `${identity}:address=${normalizePaveAdresseFlag(field.IsPaveAdresse)}`
    : null;
}

/** Maps all supported backend response shapes without mutating the response. */
export function mapPersistedChampsFusion(response: unknown): ChampsFusion[] {
  return getRawFields(response)
    .reduce<IndexedChampsFusion[]>((mapped, rawField: unknown, originalIndex: number) => {
      if (!isRecord(rawField)) {
        return mapped;
      }

      const field = rawField as RawChampsFusion;
      const fieldName = normalizeBusinessValue(
        field.ChampFusion ?? field.champFusion ?? field.FieldName ?? field.fieldName
      );
      if (!fieldName) {
        return mapped;
      }

      mapped.push({
        field: {
          Id: normalizeBusinessValue(field.Id ?? field.id) ?? undefined,
          ChampFusion: fieldName,
          internalName: normalizeBusinessValue(field.internalName ?? field.InternalName) ?? undefined,
          Sequence: normalizeSequence(field.Sequence ?? field.sequence, originalIndex),
          IsPaveAdresse: normalizePaveAdresseFlag(field.IsPaveAdresse ?? field.isPaveAdresse),
        },
        originalIndex,
      });
      return mapped;
    }, [])
    .sort((left, right) =>
      (left.field.Sequence ?? left.originalIndex) - (right.field.Sequence ?? right.originalIndex) ||
      left.originalIndex - right.originalIndex
    )
    .map(({ field }) => field);
}

export function mergePersistedChampsFusion(
  restoredChampsFusion: ChampsFusion[] = [],
  backendChampsFusion: ChampsFusion[] = []
): ChampsFusion[] {
  const merged = (restoredChampsFusion || []).map(field => ({ ...field }));
  const seen = new Set(
    merged
      .map(getPersistedFieldKey)
      .filter((key): key is string => key !== null)
  );

  (backendChampsFusion || []).forEach(field => {
    const key = getPersistedFieldKey(field);
    if (key && seen.has(key)) {
      return;
    }

    merged.push({ ...field });
    if (key) {
      seen.add(key);
    }
  });

  return merged;
}

export function resolvePersistedChampsFusion(
  hasRestoredDraft: boolean,
  restoredChampsFusion: ChampsFusion[] = [],
  backendChampsFusion: ChampsFusion[] = []
): ChampsFusion[] {
  return hasRestoredDraft
    ? mergePersistedChampsFusion(restoredChampsFusion, backendChampsFusion)
    : (backendChampsFusion || []).map(field => ({ ...field }));
}
